import {Box, Button, ButtonGroup, Typography} from "@mui/material";
import React, {useContext, useState} from "react";
import {FormContext} from "../form_buttons";
import {H3, Typographies} from "../../../utils/typography_consts";
import {DataGrid, GridColDef, GridRowId, GridRowModel} from "@mui/x-data-grid";

export interface FormDataGridProps<T> {
	title: string;
	field: string;
	columns: FormDataGridColumn<T>[];
	onRemove?: (ids: number[]) => Promise<void>;
	onAppend?: () => Promise<T>;
}

export interface FormDataGridColumn<T> {
	editable?: boolean;
	key: string;
	name: string;
	width: number | string;
}

function FormDataGrid<T>(props: FormDataGridProps<T>) {
	const formContext = useContext(FormContext);
	const model = formContext.form;
	const modelField = model.field(props.field);

	const [lines, setLines] = useState<Array<T>>((modelField.value() as T[]) || []);
	const [selectedIds, setSelectedIds] = useState<GridRowId[]>([]);

	model.register(props.field, () => lines, t => setLines(t));

	const columns: GridColDef[] = props.columns.map((col) => ({
		field: col.key,
		headerName: col.name,
		editable: col.editable ?? false,
		width: typeof col.width === "number" ? col.width : undefined,
		flex: typeof col.width === "string" ? 1 : undefined,
	}));

	const processRowUpdate = (newRow: GridRowModel) => {
		const updated = lines.map((line: any) =>
			line["id"] === newRow["id"] ? {...line, ...newRow} : line
		);
		setLines(updated as T[]);
		return newRow;
	};

	return (
		<>
			<Typography variant={Typographies.h4} component={H3}>
				{props.title}
			</Typography>

			<DataGrid
				rows={lines as any[]}
				columns={columns}
				getRowId={(row: any) => row["id"]}
				checkboxSelection
				rowSelectionModel={selectedIds}
				onRowSelectionModelChange={(ids) => setSelectedIds([...ids])}
				processRowUpdate={processRowUpdate}
				autoHeight
				disableRowSelectionOnClick
			/>

			<Box my={4}>
				<ButtonGroup>
					{props.onAppend && (
						<Button
							size={"small"}
							color={"secondary"}
							variant={"contained"}
							onClick={() => {
								props.onAppend!().then((newBean) => {
									setLines([...lines, newBean]);
								});
							}}>
							Añadir fila
						</Button>
					)}
					{props.onRemove && (
						<Button
							disabled={selectedIds.length === 0}
							size={"small"}
							color={"secondary"}
							variant={"contained"}
							onClick={() => {
								if (confirm("Seguro que desea borrar las líneas seleccionadas?")) {
									const ids = selectedIds.map(Number);
									props.onRemove!(ids).then(() => {
										setLines(lines.filter((line: any) => !ids.includes(line["id"])));
										setSelectedIds([]);
									});
								}
							}}>
							Borrar seleccionadas
						</Button>
					)}
				</ButtonGroup>
			</Box>
		</>
	);
}

export default FormDataGrid;
